import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { hasCapability } from '@hub/shared';
import { api } from '../lib/api.js';
import { SEND_MODE_LABEL, label } from '../lib/labels.js';
import { dateTime } from '../lib/format.js';
import { ENV_META, ENV_ORDER, envFromApp } from '../lib/environments.js';
import type { EnvName } from '../lib/environments.js';
import type { Me } from '../lib/session.js';
import { ConfirmDialog, Dialog, ErrorState, Icon, LoadingState, StatusBadge } from './ui.js';

interface Card {
  name: EnvName;
  allowed: boolean;
  current: boolean;
  reason?: string;
  database: string;
  sendCeiling: string;
  clockMode: string;
  blockers?: Array<{ code: string; message: string }>;
}
interface EnvData {
  multi: boolean;
  current?: EnvName;
  environments: Card[];
  lastSwitch: { to: string; at?: string; by?: string } | null;
  fallbackReason?: string;
}

/** Po přepnutí proces restartuje; čekáme, až /health ohlásí cílové prostředí. */
export function Switching({ target }: { target: string }): JSX.Element {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let stop = false;
    const startedAt = Date.now();
    const tick = async (): Promise<void> => {
      if (stop) return;
      try {
        const res = await fetch('/health', { cache: 'no-store' });
        const body = (await res.json()) as { environment?: string };
        if (res.ok && body.environment === target) {
          window.location.reload();
          return;
        }
      } catch {
        // server se právě restartuje
      }
      if (Date.now() - startedAt > 120_000) {
        setFailed(true);
        return;
      }
      setTimeout(() => void tick(), 1500);
    };
    void tick();
    return () => {
      stop = true;
    };
  }, [target]);
  const name = ENV_META[target as EnvName]?.label ?? target;
  return (
    <div className="overlay" role="alert" data-testid="switching">
      <div className="panel" style={{ maxWidth: 480, margin: '20vh auto', padding: 24 }}>
        <h2>Přepínám prostředí…</h2>
        <p>
          Hub se restartuje na prostředí <b>{name}</b>. Stránka se po dokončení sama obnoví a bude
          potřeba se znovu přihlásit.
        </p>
        {failed ? (
          <p className="note">
            Nové prostředí neodpovědělo do dvou minut. Zkontrolujte logy hubu (
            <code>scripts\hub.ps1 logs</code>).
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Obsah štítku prostředí: ikona + název (+ stručný popis); barvu dává třída env-* na rodiči. */
export function EnvBadge({
  name,
  showDescription,
}: {
  name: EnvName;
  showDescription?: boolean;
}): JSX.Element {
  const m = ENV_META[name];
  return (
    <span className="envbadge-inner">
      <Icon name={m.icon} size={16} />
      <b>{m.label}</b>
      {showDescription ? <span className="envdesc">{m.short}</span> : null}
    </span>
  );
}

/**
 * Karty všech prostředí se stavem a tlačítky pro přepnutí. Přepnout smí jen admin
 * (`canSwitch`); strop nasazení (HUB_ENVIRONMENTS_ALLOWED) UI nikdy nezvyšuje: nepovolené
 * prostředí má vypnuté tlačítko a zdůvodnění z API. Produkce vyžaduje slovo PRODUKCE.
 */
export function EnvironmentChooser({
  canSwitch,
  onSwitched,
}: {
  canSwitch: boolean;
  onSwitched: (to: string) => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<EnvName | null>(null);
  const q = useQuery<EnvData>({
    queryKey: ['environment'],
    queryFn: () => api.get('/api/environment'),
  });
  const change = useMutation({
    mutationFn: (input: { target: string; confirmation?: string }) =>
      api.post<{ to: string }>('/api/environment/switch', input),
    onSuccess: (res) => {
      setError(null);
      setAsking(null);
      onSwitched(res.to);
    },
    onError: (err: Error) => {
      setAsking(null);
      setError(err.message);
    },
  });

  if (q.isLoading) return <LoadingState what="prostředí" />;
  if (q.isError) return <ErrorState error={q.error} title="Prostředí se nepodařilo načíst" />;
  const data = q.data;
  if (!data) return <LoadingState />;
  if (!data.multi) {
    return (
      <p className="hint">
        Přepínání prostředí není v tomto nasazení zapnuté (chybí{' '}
        <code>HUB_ENVIRONMENTS_ALLOWED</code>). Hub běží pořád v jednom prostředí.
      </p>
    );
  }
  const cards = ENV_ORDER.map((n) => data.environments.find((c) => c.name === n)).filter(
    (c): c is Card => !!c,
  );
  const target = asking ? cards.find((c) => c.name === asking) : undefined;

  return (
    <>
      {error ? <ErrorState error={new Error(error)} title="Přepnutí se nepovedlo" /> : null}
      {data.fallbackReason ? <ErrorState error={new Error(data.fallbackReason)} /> : null}
      <p className="lede">
        Hub běží vždy v jednom prostředí. Každé má vlastní databázi, nastavení, uživatele i přístupy
        ke SmartEmailingu. Která prostředí jde zapnout, určuje nasazení, ne administrace. Přepnout
        „dolů“ (Produkce, Zkouška, Vývoj) jde vždy.
        {canSwitch ? '' : ' Přepínat prostředí smí jen administrátor.'}
      </p>
      <div className="envcards">
        {cards.map((c) => {
          const m = ENV_META[c.name];
          const blocked = !!c.blockers && c.blockers.length > 0;
          return (
            <section
              key={c.name}
              className={`envcard env-${c.name}${c.current ? ' current' : ''}${c.allowed ? '' : ' off'}`}
              data-testid={`env-card-${c.name}`}
              aria-label={m.label}
            >
              <header>
                <span className="envicon" aria-hidden="true">
                  <Icon name={m.icon} size={18} />
                </span>
                <h3>{m.label}</h3>
                {c.current ? (
                  <StatusBadge tone="acc" icon="check">
                    právě běží
                  </StatusBadge>
                ) : c.allowed ? (
                  <StatusBadge tone="ok">povoleno</StatusBadge>
                ) : (
                  <StatusBadge tone="neu">nepovoleno</StatusBadge>
                )}
              </header>
              <p>{m.summary}</p>
              <ul className="envfacts">
                {m.details.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
              <dl className="kv">
                <dt>Databáze</dt>
                <dd>
                  <code>{c.database}</code>
                </dd>
                <dt>Strop odesílání</dt>
                <dd>{label(SEND_MODE_LABEL, c.sendCeiling)}</dd>
                <dt>Hodiny</dt>
                <dd>{c.clockMode === 'simulated' ? 'simulované' : 'skutečné'}</dd>
              </dl>
              {!c.allowed ? (
                <p className="note" data-testid={`env-reason-${c.name}`}>
                  {c.reason ?? 'Toto nasazení prostředí nepovoluje (HUB_ENVIRONMENTS_ALLOWED).'}{' '}
                  Zapnout ho lze jen úpravou nasazení, ne z administrace.
                </p>
              ) : null}
              {blocked ? (
                <div className="note" data-testid={`env-blockers-${c.name}`}>
                  Produkci zatím nelze zapnout:
                  <ul>
                    {c.blockers?.map((b) => (
                      <li key={b.code}>{b.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {c.current ? null : (
                <div className="envaction">
                  <button
                    type="button"
                    className={`btn${c.name === 'production' ? ' dng' : ''}`}
                    disabled={!c.allowed || blocked || !canSwitch}
                    onClick={() => setAsking(c.name)}
                  >
                    Přepnout na {m.label}
                  </button>
                </div>
              )}
            </section>
          );
        })}
      </div>
      <p className="note">
        Poslední přepnutí:{' '}
        {data.lastSwitch?.at
          ? `${dateTime(data.lastSwitch.at, 'Europe/Prague')} na ${ENV_META[data.lastSwitch.to as EnvName]?.label ?? data.lastSwitch.to}${data.lastSwitch.by ? `, provedl ${data.lastSwitch.by}` : ''}`
          : 'zatím žádné'}
      </p>
      <ConfirmDialog
        open={!!target}
        title={target ? `Přepnout na ${ENV_META[target.name].label}?` : ''}
        danger={target?.name === 'production'}
        requireWord={target?.name === 'production' ? 'PRODUKCE' : undefined}
        confirmLabel={target ? `Přepnout na ${ENV_META[target.name].label}` : undefined}
        busy={change.isPending}
        onCancel={() => setAsking(null)}
        onConfirm={(word) =>
          target && change.mutate({ target: target.name, ...(word ? { confirmation: word } : {}) })
        }
      >
        {target?.name === 'production' ? (
          <p>
            Produkce odesílá zprávy <b>skutečným zákazníkům</b> a používá produkční databázi.
            Přepnutí restartuje hub a bude se nutné znovu přihlásit.
          </p>
        ) : (
          <p>
            Hub se restartuje a přepne na prostředí{' '}
            <b>{target ? ENV_META[target.name].label : ''}</b>. Přihlášení bude potřeba zopakovat.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

/** Pro role bez práva přepínat (API je jen pro administrátora): stav z /api/me, bez tlačítek. */
function EnvironmentReadOnly({ me, name }: { me: Me; name: EnvName }): JSX.Element {
  const allowed = me.environment?.allowed;
  return (
    <>
      <p className="lede">
        Hub běží vždy v jednom prostředí. Přepínat je smí jen administrátor; vy vidíte, ve kterém
        právě pracujete.
      </p>
      <div className="envcards">
        {ENV_ORDER.map((n) => {
          const m = ENV_META[n];
          const isAllowed = !allowed || allowed.includes(n);
          return (
            <section
              key={n}
              className={`envcard env-${n}${n === name ? ' current' : ''}${isAllowed ? '' : ' off'}`}
              data-testid={`env-card-${n}`}
              aria-label={m.label}
            >
              <header>
                <span className="envicon" aria-hidden="true">
                  <Icon name={m.icon} size={18} />
                </span>
                <h3>{m.label}</h3>
                {n === name ? (
                  <StatusBadge tone="acc" icon="check">
                    právě běží
                  </StatusBadge>
                ) : isAllowed ? (
                  <StatusBadge tone="ok">povoleno</StatusBadge>
                ) : (
                  <StatusBadge tone="neu">nepovoleno</StatusBadge>
                )}
              </header>
              <p>{m.summary}</p>
              <ul className="envfacts">
                {m.details.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </>
  );
}

/** Štítek v záhlaví: vždy vidět; klik otevře přepínač prostředí. */
export function EnvironmentSwitcher({ me }: { me: Me }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const name: EnvName = me.environment?.name ?? envFromApp(me.appEnv);
  const m = ENV_META[name];
  const canSwitch = hasCapability(me.user.departments, 'switch_environment');
  if (target) return <Switching target={target} />;
  return (
    <>
      <button
        type="button"
        className={`envbadge env-${name}`}
        data-testid="environment-banner"
        aria-haspopup="dialog"
        title={`Prostředí: ${m.label} (${m.short}). Klikněte pro přehled prostředí.`}
        onClick={() => setOpen(true)}
      >
        <EnvBadge name={name} showDescription />
        {me.environment ? (
          <span className="envdb">
            databáze <code>{me.environment.database}</code>
          </span>
        ) : null}
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Prostředí hubu" wide>
        {canSwitch ? (
          <EnvironmentChooser
            canSwitch
            onSwitched={(to) => {
              setOpen(false);
              setTarget(to);
            }}
          />
        ) : (
          <EnvironmentReadOnly me={me} name={name} />
        )}
      </Dialog>
    </>
  );
}
